import type { Capitolo, GruppoCapitoli, Permesso } from './tipi.ts'

/**
 * Indice della Guida.
 *
 * Un capitolo nuovo: un file in capitoli/ che esporta il capitolo (`export default`), con `id`
 * uguale al nome del file, e una riga in ORDINE. Un file dimenticato in ORDINE non si perde: va in
 * coda al suo gruppo.
 */
const ORDINE = [
  'accesso',
  'cruscotto',
  'telefono',
  'palestra',
  'beneficiari',
  'nuova-scheda',
  'scheda',
  'consensi',
  'anonimizzate',
  'percorsi-interventi',
  'agenda',
  'area-riservata',
  'attivita-collettive',
  'mediazioni',
  'albo-mediatori',
  'enti',
  'registro-servizi',
  'progetti',
  'messenger',
  'posta',
  'report-esportazioni',
  'utenti',
  'approvazioni',
  'formazione',
]

export const GRUPPI: GruppoCapitoli[] = [
  'Per iniziare',
  'Le schede delle persone',
  'Il lavoro con le persone',
  'Mediazioni',
  'Rete e progetti',
  'Comunicazione',
  'Report e gestione',
]

const FILE = import.meta.glob<{ default: Capitolo }>('./capitoli/*.ts', { eager: true })

const posizione = (id: string) => {
  const i = ORDINE.indexOf(id)
  return i < 0 ? ORDINE.length : i
}

export const CAPITOLI: Capitolo[] = Object.values(FILE)
  .map((m) => m.default)
  .sort((a, b) => GRUPPI.indexOf(a.gruppo) - GRUPPI.indexOf(b.gruppo) || posizione(a.id) - posizione(b.id))

/** Stessa regola delle voci di menu: senza permesso tutti, altrimenti basta uno di quelli indicati. */
export function visibile(permesso: Permesso | undefined, puo: (p: string) => boolean): boolean {
  if (!permesso) return true
  return Array.isArray(permesso) ? permesso.some((p) => puo(p)) : puo(permesso)
}

/** Il capitolo con le sole parti che l'utente vede. */
export function perUtente(c: Capitolo, puo: (p: string) => boolean): Capitolo {
  return { ...c, parti: c.parti.filter((p) => visibile(p.permesso, puo)) }
}

/** I capitoli che servono all'utente, nell'ordine dell'indice. */
export function capitoliPer(puo: (p: string) => boolean): Capitolo[] {
  return CAPITOLI.filter((c) => visibile(c.permesso, puo)).map((c) => perUtente(c, puo))
}

// --- ricerca -----------------------------------------------------------------------------------

/** Minuscole, senza accenti, senza virgolette e grassetti: «Modifica» e modifica si trovano uguali. */
export function normalizza(testo: string): string {
  return testo.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[«»*’'"]/g, ' ')
}

/** Le frasi di un capitolo in cui cercare, ognuna con il titolo della parte in cui sta. */
function frasi(c: Capitolo): { dove: string; testo: string }[] {
  const righe = [
    { dove: c.titolo, testo: c.titolo },
    { dove: c.titolo, testo: c.aCheServe },
    { dove: c.titolo, testo: (c.parole ?? []).join(' ') },
  ]
  for (const p of c.parti) {
    righe.push({ dove: p.titolo, testo: p.titolo })
    for (const t of [...(p.testo ?? []), ...(p.passi ?? []), p.nota ?? '']) righe.push({ dove: p.titolo, testo: t })
  }
  for (const a of c.avvertenze) righe.push({ dove: 'Avvisi ed errori comuni', testo: `${a.quando} ${a.cosaFare}` })
  return righe.filter((r) => r.testo)
}

export type Risultato = { capitolo: Capitolo; dove: string[] }

/**
 * Cerca fra i capitoli visibili: un capitolo esce se contiene tutte le parole scritte, anche in parti
 * diverse. `dove`: le parti in cui compare almeno una parola, per dire dove guardare.
 */
export function cerca(domanda: string, capitoli: Capitolo[]): Risultato[] {
  const parole = normalizza(domanda).split(/\s+/).filter((p) => p.length > 1)
  if (!parole.length) return []
  const risultati: Risultato[] = []
  for (const c of capitoli) {
    const righe = frasi(c).map((r) => ({ ...r, norm: normalizza(r.testo) }))
    const tutto = righe.map((r) => r.norm).join(' ')
    if (!parole.every((p) => tutto.includes(p))) continue
    const dove = [...new Set(righe.filter((r) => parole.some((p) => r.norm.includes(p)) && r.dove !== c.titolo)
      .map((r) => r.dove))]
    risultati.push({ capitolo: c, dove: dove.slice(0, 4) })
  }
  // Prima i capitoli che hanno le parole nel titolo.
  return risultati.sort((a, b) => Number(parole.some((p) => normalizza(b.capitolo.titolo).includes(p)))
    - Number(parole.some((p) => normalizza(a.capitolo.titolo).includes(p))))
}
